import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Alert, alpha, Box, BugReportIcon, Button, CloudOffIcon, Collapse, ContentCopyIcon, Divider, ExpandLessIcon, ExpandMoreIcon, HomeIcon, LockIcon, MemoryIcon, Paper, RefreshIcon, SpeedIcon, Stack, TimerIcon, Typography, useTheme, WifiOffIcon } from '@/shared/ui/mui';
import type { Theme } from '@/shared/ui/mui';
import { logger } from '@/shared/utils/logger';
import {
  categorizeError,
  generateErrorId,
  getDefaultRecoveryStrategy,
} from './ErrorBoundary';
import type { ErrorCategory } from './types';

interface ErrorVisualConfig {
  /** Key under `errors:categories.*` for the title/message copy. */
  key: string;
  icon: ReactNode;
  tone: 'error' | 'warning' | 'info';
}

/**
 * Maps error categories to a translation key and visual tone.
 * Title/message copy is resolved via i18next at render time.
 */
const getErrorVisual = (category: ErrorCategory): ErrorVisualConfig => {
  switch (category) {
    case 'network':
      return { key: 'network', icon: <WifiOffIcon fontSize="large" />, tone: 'info' };
    case 'auth':
      return { key: 'auth', icon: <LockIcon fontSize="large" />, tone: 'warning' };
    case 'permission':
      return { key: 'permission', icon: <LockIcon fontSize="large" />, tone: 'warning' };
    case 'chunk-load':
      return { key: 'chunkLoad', icon: <RefreshIcon fontSize="large" />, tone: 'info' };
    case 'memory':
      return { key: 'memory', icon: <MemoryIcon fontSize="large" />, tone: 'warning' };
    case 'validation':
      return { key: 'validation', icon: <BugReportIcon fontSize="large" />, tone: 'warning' };
    case 'timeout':
      return { key: 'timeout', icon: <TimerIcon fontSize="large" />, tone: 'warning' };
    case 'rate-limit':
      return { key: 'rateLimit', icon: <SpeedIcon fontSize="large" />, tone: 'warning' };
    case 'server-error':
      return { key: 'serverError', icon: <CloudOffIcon fontSize="large" />, tone: 'error' };
    default:
      return { key: 'unknown', icon: <BugReportIcon fontSize="large" />, tone: 'error' };
  }
};

const getToneColor = (
  tone: ErrorVisualConfig['tone'],
  theme: Theme,
): string => {
  if (tone === 'info') return theme.palette.info.main;
  if (tone === 'warning') return theme.palette.warning.main;
  return theme.palette.error.main;
};

export const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const [expanded, setExpanded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation('errors');
  const isDevelopment = import.meta.env.DEV;
  const MAX_RETRIES = 3;

  // Use boundary-generated ID when available; otherwise fall back to local generation.
  const boundaryErrorId = (error as Error & { __errorId?: string })?.__errorId;
  const errorId = useMemo(
    () => boundaryErrorId || generateErrorId(),
    [boundaryErrorId],
  );

  // Categorize error and get recovery strategy
  const errorCategory = error ? categorizeError(error) : 'unknown';
  const recoveryStrategy = getDefaultRecoveryStrategy(errorCategory);
  const { key: categoryKey, icon, tone } = getErrorVisual(errorCategory);
  const title = t(`categories.${categoryKey}.title`);
  const message = t(`categories.${categoryKey}.message`);
  const toneColor = getToneColor(tone, theme);

  // Log error details in development
  useEffect(() => {
    if (isDevelopment && error) {
      logger.error('Error caught by ErrorFallback:', {
        errorId,
        category: errorCategory,
        message: error.message,
        stack: error.stack,
        retryCount,
      });
    }
  }, [error, isDevelopment, retryCount, errorId, errorCategory]);

  const handleRetry = useCallback(() => {
    if (retryCount >= MAX_RETRIES || !recoveryStrategy.canRetry) {
      return;
    }

    setIsRetrying(true);
    setRetryCount(prev => prev + 1);

    // For chunk loading errors, reload the page
    if (recoveryStrategy.shouldReloadPage) {
      window.location.reload();
      return;
    }

    // For auth errors, clear auth state from localStorage
    if (recoveryStrategy.shouldClearAuth) {
      try {
        localStorage.removeItem('auth-storage');
      } catch {
        // Ignore localStorage errors
      }
    }

    // Implement exponential backoff for retries
    const delay = Math.min(1000 * 2 ** retryCount, 5000);

    setTimeout(() => {
      setIsRetrying(false);
      resetErrorBoundary();
    }, delay);
  }, [retryCount, recoveryStrategy, resetErrorBoundary]);

  const handleGoHome = useCallback(() => {
    window.location.href = '/';
  }, []);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  const handleCopyId = useCallback(async () => {
    if (!navigator.clipboard) return;

    try {
      await navigator.clipboard.writeText(errorId);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Ignore clipboard errors
    }
  }, [errorId]);

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `radial-gradient(circle at 20% 10%, ${alpha(
          theme.palette.primary.main,
          0.1,
        )} 0%, ${theme.palette.background.default} 45%)`,
        p: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: { xs: 3, sm: 4 },
          maxWidth: 680,
          width: '100%',
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
          textAlign: 'center',
        }}
      >
        <Stack spacing={2.5}>
          <Box
            sx={{
              width: 76,
              height: 76,
              borderRadius: 2.5,
              backgroundColor: alpha(toneColor, 0.12),
              color: toneColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              border: `1px solid ${alpha(toneColor, 0.25)}`,
            }}
          >
            {icon}
          </Box>

          <Box>
            <Typography
              variant="overline"
              sx={{
                color: toneColor,
                letterSpacing: 1.2,
                fontWeight: 700,
              }}
            >
              {t('fallback.applicationError')}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {title}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 1, maxWidth: 560, mx: 'auto' }}
            >
              {message}
            </Typography>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            justifyContent="center"
          >
            {recoveryStrategy.canRetry && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleRetry}
                startIcon={<RefreshIcon />}
                disabled={retryCount >= MAX_RETRIES || isRetrying}
              >
                {isRetrying
                  ? t('fallback.retrying')
                  : retryCount >= MAX_RETRIES
                    ? t('fallback.maxRetriesReached')
                    : t('fallback.tryAgain')}
              </Button>
            )}

            {recoveryStrategy.shouldReloadPage && (
              <Button
                variant={recoveryStrategy.canRetry ? 'outlined' : 'contained'}
                color="primary"
                onClick={handleReload}
                startIcon={<RefreshIcon />}
              >
                {t('fallback.reloadPage')}
              </Button>
            )}

            <Button
              variant="outlined"
              onClick={handleGoHome}
              startIcon={<HomeIcon />}
            >
              {t('fallback.goHome')}
            </Button>
          </Stack>

          {retryCount > 0 && retryCount < MAX_RETRIES && (
            <Alert severity="info">
              {t('fallback.retryAttempt', { count: retryCount, max: MAX_RETRIES })}
            </Alert>
          )}

          {retryCount >= MAX_RETRIES && (
            <Alert severity="warning">{t('fallback.maxRetriesAlert')}</Alert>
          )}

          {isDevelopment && error && (
            <>
              <Button
                size="small"
                onClick={() => setExpanded(!expanded)}
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ alignSelf: 'center', color: 'text.secondary' }}
              >
                {expanded ? t('fallback.hideDetails') : t('fallback.showDetails')}
              </Button>

              <Collapse in={expanded}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    backgroundColor: alpha(theme.palette.grey[500], 0.08),
                    borderRadius: 2,
                    overflow: 'auto',
                    maxHeight: 280,
                    textAlign: 'left',
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom fontWeight={700}>
                    {t('fallback.errorType', { type: error.name || 'Unknown' })}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace',
                      fontSize: '0.8125rem',
                      color: theme.palette.error.dark,
                      m: 0,
                    }}
                  >
                    {error.message || t('fallback.noMessage')}
                    {error.stack ? `\n\nStack Trace:\n${error.stack}` : ''}
                  </Typography>
                </Paper>
              </Collapse>
            </>
          )}

          <Divider />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'center', sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'left' }}>
              {t('fallback.contactSupport')}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                component="code"
                translate="no"
                sx={{
                  backgroundColor: alpha(theme.palette.grey[500], 0.18),
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                {errorId}
              </Box>
              <Button
                size="small"
                variant="text"
                onClick={handleCopyId}
                startIcon={<ContentCopyIcon fontSize="small" />}
              >
                {isCopied ? t('fallback.copied') : t('fallback.copyId')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};
